import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { featuredToolIds, recentToolIds } from '../registry/tools'
import {
  defaultCagrInput,
  defaultEmiInput,
  defaultSipInput,
  normalizeRememberedCagr,
  normalizeRememberedEmi,
  normalizeRememberedSip,
  type RememberedCagrInput,
  type RememberedEmiInput,
  type RememberedSipInput,
  type SavedSipPlan,
} from './memory'

export type { RememberedCagrInput, RememberedEmiInput, RememberedSipInput, SavedSipPlan }

type DrawerState = {
  displayName: string | null
  pinnedToolIds: string[]
  recentToolIds: string[]
  lastSipInput: RememberedSipInput | null
  lastCagrInput: RememberedCagrInput | null
  lastEmiInput: RememberedEmiInput | null
  savedSipPlans: SavedSipPlan[]
  togglePin: (toolId: string) => void
  openTool: (toolId: string) => void
  clearRecents: () => void
  rememberSip: (input: RememberedSipInput) => void
  rememberCagr: (input: RememberedCagrInput) => void
  rememberEmi: (input: RememberedEmiInput) => void
  setDisplayName: (name: string) => void
  saveSipPlan: (name: string, input: RememberedSipInput) => boolean
  removeSipPlan: (id: string) => void
}

const initialPinned = [...featuredToolIds]

export const useDrawerStore = create<DrawerState>()(
  persist(
    (set) => ({
      displayName: null,
      pinnedToolIds: initialPinned,
      recentToolIds: [...recentToolIds],
      lastSipInput: null,
      lastCagrInput: null,
      lastEmiInput: null,
      savedSipPlans: [],
      togglePin: (toolId) =>
        set((state) => ({
          pinnedToolIds: state.pinnedToolIds.includes(toolId)
            ? state.pinnedToolIds.filter((id) => id !== toolId)
            : [...state.pinnedToolIds, toolId].slice(0, 8),
        })),
      openTool: (toolId) =>
        set((state) => ({
          recentToolIds: [toolId, ...state.recentToolIds.filter((id) => id !== toolId)].slice(0, 5),
        })),
      clearRecents: () => set({ recentToolIds: [] }),
      rememberSip: (input) => set({ lastSipInput: input }),
      rememberCagr: (input) => set({ lastCagrInput: input }),
      rememberEmi: (input) => set({ lastEmiInput: input }),
      setDisplayName: (name) => set({ displayName: name.trim().slice(0, 40) || null }),
      saveSipPlan: (name, input) => {
        let saved = false
        set((state) => {
          if (state.savedSipPlans.length >= 20) return state
          saved = true
          return { savedSipPlans: [...state.savedSipPlans, { id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`, name, input: normalizeRememberedSip(input), savedAt: new Date().toISOString() }] }
        })
        return saved
      },
      removeSipPlan: (id) => set((state) => ({ savedSipPlans: state.savedSipPlans.filter((plan) => plan.id !== id) })),
    }),
    {
      name: 'dailydrawer.dashboard.v1',
      version: 3,
      migrate: (persisted) => {
        const state = persisted as Partial<DrawerState>
        return {
          displayName: state.displayName ?? null,
          pinnedToolIds: state.pinnedToolIds ?? initialPinned,
          recentToolIds: state.recentToolIds ?? [...recentToolIds],
          lastSipInput: state.lastSipInput ? normalizeRememberedSip(state.lastSipInput) : null,
          lastCagrInput: state.lastCagrInput ? normalizeRememberedCagr(state.lastCagrInput) : null,
          lastEmiInput: state.lastEmiInput ? normalizeRememberedEmi(state.lastEmiInput) : null,
          savedSipPlans: (state.savedSipPlans ?? []).map((plan) => ({ ...plan, input: normalizeRememberedSip(plan.input) })),
        }
      },
      partialize: (state) => ({
        displayName: state.displayName,
        pinnedToolIds: state.pinnedToolIds,
        recentToolIds: state.recentToolIds,
        lastSipInput: state.lastSipInput,
        lastCagrInput: state.lastCagrInput,
        lastEmiInput: state.lastEmiInput,
        savedSipPlans: state.savedSipPlans,
      }),
    },
  ),
)

export { defaultCagrInput, defaultEmiInput, defaultSipInput, normalizeRememberedCagr, normalizeRememberedEmi, normalizeRememberedSip }
